<template>
  <v-card color="#111827">
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      <v-toolbar-title>Analyse der Ausgaben</v-toolbar-title>
    </v-toolbar>

    <v-card-text class="pt-4">
      <v-tabs v-model="currentView" background-color="transparent" dark grow>
        <v-tab href="#chart">
          <v-icon left>mdi-chart-bar</v-icon>
          Grafische Auswertung
        </v-tab>
        <v-tab href="#table">
          <v-icon left>mdi-table</v-icon>
          Tabellarische Ansicht
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="currentView" class="transparent mt-4">
        <v-tab-item value="chart">
          <v-card color="#1F2937" class="pa-4 rounded-lg">
            <v-select
              v-model="selectedStudio"
              :items="studioSelectItems"
              label="Studio auswählen"
              outlined
              dense
              dark
              hide-details
              class="mb-4 d-inline-block mr-4"
              style="max-width: 300px"></v-select>

            <v-select
              v-model="selectedCategory"
              :items="categories"
              label="Kategorie filtern"
              outlined
              dense
              dark
              hide-details
              class="mb-4 d-inline-block"
              style="max-width: 300px"></v-select>

            <apexchart height="500" type="bar" :series="chartSeries" :options="chartOptions"></apexchart>
          </v-card>
        </v-tab-item>
        <v-tab-item value="table">
          <v-card color="#1F2937" class="pa-2 rounded-lg">
            <v-data-table :headers="tableHeaders" :items="flatInvoiceList" :search="search" dark class="elevation-0 transparent">
              <template v-slot:top>
                <div class="d-flex align-center mx-4 mt-2">
                  <v-select
                    v-model="selectedStudio"
                    :items="studioSelectItems"
                    label="Studio"
                    outlined
                    dense
                    dark
                    hide-details
                    class="mr-2"
                    style="max-width: 200px"></v-select>
                  <v-select
                    v-model="selectedCategory"
                    :items="categories"
                    label="Kategorie"
                    outlined
                    dense
                    dark
                    hide-details
                    class="mr-2"
                    style="max-width: 200px"></v-select>
                  <v-text-field v-model="search" label="Suche..." dark clearable outlined dense hide-details></v-text-field>
                </div>
              </template>
              <template v-slot:item.date="{ item }">
                {{ new Date(item.date).toLocaleDateString("de-CH") }}
              </template>
              <template v-slot:item.dueDate="{ item }">
                <span :class="{ 'grey--text': !item.dueDate }">
                  {{ item.dueDate ? new Date(item.dueDate).toLocaleDateString("de-CH") : new Date(item.date).toLocaleDateString("de-CH") + "*" }}
                </span>
              </template>

              <template v-slot:item.category="{ item }">
                <span>{{ item.category }} {{ item.isWage ? (item.temporary ? "(Lohn Vormonat)" : "(Lohn)") : "" }}</span>
              </template>

              <template v-slot:item.invoiceIssuerName="{ item }">
                <div class="d-flex align-center">
                  <v-icon
                    v-if="item.invoiceIssuer && item.invoiceIssuer.isRecurring"
                    small
                    color="blue lighten-1"
                    class="mr-2"
                    title="Dauerauftrag (automatisch generiert)">
                    mdi-sync
                  </v-icon>

                  {{ item.invoiceIssuerName }}
                </div>
              </template>
              <template v-slot:item.amount="{ item }">
                <span class="font-weight-bold">{{ formatCurrency(item.amount) }}</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-icon v-if="!item.temporary" small class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
                <v-icon small v-if="item.type === 'document'" @click="downloadItem(item)">mdi-download</v-icon>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>

    <!-- Dialogs -->
    <DocumentDialog
      v-if="editDocumentDialog"
      :hideDescription="true"
      :invoiceIssuerOptions="invoiceIssuers"
      :selectedInvoiceIssuer="itemToEdit.invoiceIssuer ? itemToEdit.invoiceIssuer._id : null"
      :dialog="editDocumentDialog"
      :document-data="itemToEdit"
      :invoice-issuer-options="invoiceIssuers"
      :issuer-search="issuerSearch"
      @close-document="editDocumentDialog = false"
      @save-document="onSaveDocument"
      @create-new-invoice-issuer="createNewIssuer" />

    <v-dialog v-model="editExpenseDialog" max-width="700px" persistent>
      <ExpenseCaptureForm
        v-if="editExpenseDialog"
        :studios="studios"
        :invoice-issuers="invoiceIssuers"
        :initial-data="itemToEdit"
        @close="editExpenseDialog = false"
        @expense-saved="onSaveExpense" />
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"
import DocumentDialog from "../../documents/components/documentDialog.vue"
import ExpenseCaptureForm from "./ExpenseCaptureForm.vue"

export default {
  name: "InvoiceAnalysis",
  components: { DocumentDialog, ExpenseCaptureForm },
  props: {
    stats: { type: Object, required: true },
  },
  data() {
    return {
      invoiceIssuerOptions: [],
      currentView: "chart",
      search: "",
      selectedStudio: "Gesamt",
      selectedCategory: "Alle", // NEU
      // Data for Dialogs
      invoiceIssuers: [],
      studios: [], // Needed for ExpenseCaptureForm
      // Edit State
      editDocumentDialog: false,
      editExpenseDialog: false,
      itemToEdit: {},
      issuerSearch: "",

      tableHeaders: [
        { text: "Studio", value: "studioName" },
        { text: "Datum", value: "date" },
        { text: "Fälligkeit", value: "dueDate" },
        { text: "Kategorie", value: "category" },

        { text: "Titel / Rechnungssteller", value: "invoiceIssuerName" },
        { text: "Rechnungsnummer", value: "invoiceNumber" },
        { text: "Betrag", value: "amount", align: "end" },
        { text: "Aktionen", value: "actions", sortable: false, align: "end" },
      ],
      // Categories Hardcoded (or could be extracted)
      categories: [
        "Alle",
        "Leistungen Dritter",
        "Personalaufwand",
        "Kreditkarten",
        "RAUMAUFWAND",
        "ENERGIE- UND ENTSORGUNGSAUFWAND",
        "SACHVERSICH., ABGABEN, GEBÜHREN, BEWILLIGUNGEN",
        "FAHRZEUG- UND TRANSPORTAUFWAND",
        "WERBEAUFWAND",
        "Reisespesen/Kundenbetreuung",
        "Reparaturen / Wartungen",
        "VERWALTUNGS-AUFWAND",
        "INFORMATIKAUFWAND",
        "Beratungskosten",
        "Lizenzen",
        "ÜBRIGER BETRIEBSAUFWAND",
        "Forderungsverluste",
        "Fitnessgeräte und Solarien",
        "Sauna / Dampfbad",
        "Handelswaren",
        "Leasing Maschinen",
        "Steuern",
        "Mobilien und Einrichtungen",
        "Büromobiliar/Büro-/EDV-Geräte",
        "Fahrzeuge",
        "Unbekannt",
      ],
    }
  },
  async created() {
    // Fetch Data for Dialogs

    try {
      const [issuers, studios] = await Promise.all([
        api.invoiceIssuers.fetch(),
        // Assuming there is an API for studios, but stats usually has studio names.
        // ExpenseCaptureForm expects 'studios' prop array of objects {name, _id}.
        // We can try to fetch them or assume the parent passes them?
        // RecurringExpenseManager receives studios as prop. InvoiceAnalysis receives only stats.
        // I should fetch studios if possible.
        // But api.js doesn't show a direct 'fetchStudios'.
        // documents_backend.js uses 'Studio' model.
        // Check api.js content again... it has 'storeStudios' in vuex in DocumentDialog.
        // I will try to fetch folders or similar? No.
        // I will rely on what I can get. If I can't fetch studios, I might have a problem for the Dropdown in ExpenseCapture.
        // But wait, RecurringExpenseManager has: props: { studios: ... }
        // InvoiceAnalysis should probably also have it.
        // For now, I'll try to use a mock or fetch if I find the endpoint.
        // Actually, DocumentDialog uses Vuex 'studioStore'.
        // ExpenseCaptureForm expects prop 'studios'.
      ])
      this.invoiceIssuers = issuers
      // I will assume I can get studios from somewhere or pass them as prop?
      // The user didn't mention updating the parent of InvoiceAnalysis.
      // I will add a method to fetch studios if it exists in api.
      // api.js has `fetchFolders` but not `fetchStudios`.
      // However, `ExpenseCaptureForm.vue` uses `studios` prop.
      // Let's check `documentsApi.js` again...
    } catch (e) {
      console.error("Error fetching data", e)
    }
    // Since I cannot be sure about studios, I will try to extract them from stats?
    // No, stats only has names. I need IDs.
    // I will try to fetch studios via a new endpoint if I could, but I can't modify API easily now (already did backend).
    // Actually, I can use the `documents` endpoint? No.
    // Use `api.documents.fetchFolders`? No.
    // I will rely on `api.invoiceIssuers.fetch()` which is defined.
    // For Studios, I will use a placeholder or try to use `this.$store.state.studioStore.storeStudios` if available?
    if (this.$store && this.$store.state.studioStore) {
      this.studios = this.$store.state.studioStore.storeStudios
    }
  },
  methods: {
    formatCurrency(value) {
      if (typeof value !== "number") return "0.00 CHF"
      return `${value.toFixed(2)} CHF`
    },
    // Actions
    editItem(item) {
      console.log(item)
      this.itemToEdit = { ...item }
      this.itemToEdit.studio = this.itemToEdit.studioId || null
      // Ensure date objects are Dates
      if (this.itemToEdit.date) this.itemToEdit.date = new Date(this.itemToEdit.date).toISOString().substr(0, 10)
      if (this.itemToEdit.dueDate) this.itemToEdit.dueDate = new Date(this.itemToEdit.dueDate).toISOString().substr(0, 10)

      // Check type
      if (item.type === "document") {
        // DocumentDialog expects specific format
        // And it expects an object with _id, title, etc.
        // item has documentId as _id.
        this.itemToEdit._id = item.documentId
        this.editDocumentDialog = true
      } else if (item.type === "expense") {
        // HIER GEÄNDERT: Erst prüfen ob invoiceIssuer existiert (oder Optional Chaining '?.' nutzen)
        if (item.invoiceIssuer?.isRecurring) {
          this.$toast.info("Dauerauftrag bei den Daueraufträgen bearbeiten")
          return
        }
        this.itemToEdit._id = item.documentId
        this.editExpenseDialog = true
      } else {
        // Fallback or assume document
        this.itemToEdit._id = item.documentId
        this.editDocumentDialog = true
      }
    },
    async downloadItem(item) {
      try {
        await api.documents.downloadDocument(item.documentId, item.title + ".pdf")
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Download")
      }
    },
    async onSaveDocument(docData, issuer) {
      try {
        await api.documents.updateDocument(docData._id, docData)
        this.editDocumentDialog = false
        this.$emit("refresh") // Request parent to refresh stats
        this.$toast.success("Dokument aktualisiert")
      } catch (e) {
        this.$toast.error("Fehler beim Speichern")
      }
    },
    async onSaveExpense() {
      this.editExpenseDialog = false
      this.$emit("refresh")
      this.$toast.success("Ausgabe gespeichert")
    },
    createNewIssuer(title) {
      // Logic to create new issuer if needed
    },
  },
  computed: {
    flatInvoiceList() {
      if (!this.stats?.detailedInvoicesPerStudio) return []

      let list = this.stats.detailedInvoicesPerStudio.flatMap((studioGroup) =>
        studioGroup.invoices.map((invoice) => {
          // Bestimme den Anzeigenamen als reinen String
          const rawIssuer = invoice.invoiceIssuer
          const name = rawIssuer && rawIssuer.title ? rawIssuer.title : rawIssuer || invoice.title || "Unbekannt"

          return {
            ...invoice,
            studioName: studioGroup.studioName,
            invoiceIssuer: rawIssuer, // Original-Objekt behalten für Logik (editItem)
            invoiceIssuerName: name, // Neuer String für Suche & Sortierung
            category: invoice.category, // Sicherstellen, dass Category da ist
          }
        }),
      )

      // Filter by Studio (Existing)
      if (this.selectedStudio !== "Gesamt") {
        list = list.filter((i) => i.studioName === this.selectedStudio)
      }

      // Filter by Category (New)
      if (this.selectedCategory !== "Alle") {
        list = list.filter((i) => i.category === this.selectedCategory)
      }

      // Sort by Due Date (fallback to Date)
      list.sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate) : new Date(a.date)
        const dateB = b.dueDate ? new Date(b.dueDate) : new Date(b.date)
        return dateB - dateA
      })
      return list
    },

    // NEU: Erstellt die Liste für das Dropdown-Menü
    studioSelectItems() {
      if (!this.stats?.monthlyTotalsPerStudio) return ["Gesamt"]
      const studioNames = this.stats.monthlyTotalsPerStudio.map((s) => s.studioName)
      return ["Gesamt", ...studioNames.sort()]
    },

    // KORRIGIERT: chartSeries ist jetzt dynamisch basierend auf der Auswahl
    chartSeries() {
      // Wir starten mit einem Array aus 12 Nullen (für Jan bis Dez)
      const monthlyTotals = new Array(12).fill(0)

      // Wir nutzen flatInvoiceList, da diese Liste bereits
      // nach Studio UND Kategorie gefiltert ist!
      this.flatInvoiceList.forEach((invoice) => {
        if (!invoice.date || !invoice.amount) return

        // Datum parsen
        const date = new Date(invoice.date)
        const monthIndex = date.getMonth() // 0 = Januar, 11 = Dezember

        // Betrag zum entsprechenden Monat addieren
        // Wir nehmen an, invoice.amount ist eine Zahl.
        monthlyTotals[monthIndex] += invoice.amount
      })

      // Runden auf 2 Nachkommastellen, damit es sauber aussieht
      const roundedTotals = monthlyTotals.map((val) => Number(val.toFixed(2)))

      // Dynamischer Name für die Legende
      let seriesName = "Ausgaben"
      if (this.selectedStudio === "Gesamt") {
        seriesName = `Gesamt (${this.selectedCategory === "Alle" ? "Alle Kategorien" : this.selectedCategory})`
      } else {
        seriesName = `${this.selectedStudio} (${this.selectedCategory === "Alle" ? "Alle" : this.selectedCategory})`
      }

      return [{ name: seriesName, data: roundedTotals }]
    },

    chartOptions() {
      // Deine bestehenden Options waren gut, hier nur zur Vollständigkeit
      return {
        chart: { type: "bar", foreColor: "#9CA3AF", toolbar: { show: true } },
        plotOptions: { bar: { horizontal: false, columnWidth: "75%", dataLabels: { position: "top" } } },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
          categories: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
        },
        yaxis: { title: { text: "Ausgaben (CHF)" }, labels: { formatter: (val) => this.formatCurrency(val) } },
        fill: { opacity: 1 },
        tooltip: { theme: "dark", y: { formatter: (val) => this.formatCurrency(val) } },
        grid: { borderColor: "#374151" },
        legend: { position: "top", horizontalAlign: "left" },
      }
    },
  },
}
</script>
